import qz from 'qz-tray'

/**
 * QZ Tray connection handling (AROS-1996).
 *
 * QZ Tray is a locally installed agent that lets the browser print silently
 * to a printer selected by name, bypassing the OS print dialog. Requests are
 * signed with an RSA-SHA512 certificate/private-key pair so that QZ Tray's
 * "Remember this decision" checkbox actually works: the cashier approves the
 * untrusted-certificate prompt once per machine and is never prompted again.
 * This is the normal operating mode.
 *
 * If the public certificate can't be fetched, `VITE_QZ_PRIVATE_KEY` isn't
 * configured, or WebCrypto signing fails for any reason, the module falls
 * back to unsigned mode (certificate/signature promises resolve to `''`).
 * Printing still works in that case, but QZ Tray shows its "Allow" prompt on
 * every print instead of remembering the decision — a degraded fallback, not
 * an error state.
 *
 * The connection is lazy: nothing talks to QZ Tray until `connectQz()` is
 * called (from the first print attempt or when the printer settings dialog
 * opens), and concurrent callers share the same in-flight connection.
 */

let securityConfigured = false
let connectingPromise: Promise<void> | null = null
let cachedPrivateKeyPromise: Promise<CryptoKey | null> | null = null

const CERTIFICATE_URL = '/qz-certificate.txt'
const SIGN_ALGORITHM: RsaHashedImportParams = {
  name: 'RSASSA-PKCS1-v1_5',
  hash: 'SHA-512',
}

const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/** Imports the signing private key once and caches the CryptoKey. Resolves `null` on any failure. */
const getPrivateKey = (): Promise<CryptoKey | null> => {
  if (!cachedPrivateKeyPromise) {
    cachedPrivateKeyPromise = (async () => {
      const base64Key = import.meta.env.VITE_QZ_PRIVATE_KEY
      if (!base64Key) return null

      try {
        const keyData = base64ToArrayBuffer(base64Key)
        return await crypto.subtle.importKey(
          'pkcs8',
          keyData,
          SIGN_ALGORITHM,
          false,
          ['sign']
        )
      } catch {
        return null
      }
    })()
  }
  return cachedPrivateKeyPromise
}

const configureSecurity = () => {
  if (securityConfigured) return

  // Signed mode requires the private key to be baked into the build (Vite
  // inlines `import.meta.env.VITE_QZ_PRIVATE_KEY` at build time — a runtime
  // .env on the server is NOT read). When the key is absent, stay in plain
  // unsigned mode: resolve empty cert/signature and DO NOT declare a
  // signature algorithm. Declaring SHA512 with an empty signature makes QZ
  // Tray reject it as "Invalid Signature" instead of showing the ordinary
  // unsigned "Allow" prompt, so the key's presence gates the whole setup.
  const hasPrivateKey = Boolean(import.meta.env.VITE_QZ_PRIVATE_KEY)

  if (!hasPrivateKey) {
    qz.security.setCertificatePromise((resolve) => resolve(''))
    qz.security.setSignaturePromise(() => (resolve) => resolve(''))
    securityConfigured = true
    return
  }

  qz.security.setCertificatePromise((resolve) => {
    fetch(CERTIFICATE_URL)
      .then((res) =>
        res.ok
          ? res.text()
          : Promise.reject(new Error('Failed to fetch QZ certificate'))
      )
      .then((certificate) => resolve(certificate))
      .catch(() => resolve(''))
  })

  qz.security.setSignaturePromise((toSign) => (resolve) => {
    getPrivateKey()
      .then((privateKey) => {
        if (!privateKey) {
          resolve('')
          return undefined
        }
        return crypto.subtle
          .sign(
            SIGN_ALGORITHM.name,
            privateKey,
            new TextEncoder().encode(toSign)
          )
          .then((signature) => resolve(arrayBufferToBase64(signature)))
      })
      .catch(() => resolve(''))
  })

  qz.security.setSignatureAlgorithm('SHA512')

  securityConfigured = true
}

/** Whether an active QZ Tray websocket connection currently exists. */
export const isQzConnected = (): boolean => {
  try {
    return qz.websocket.isActive()
  } catch {
    return false
  }
}

/**
 * Connects to QZ Tray if not already connected. Safe to call from multiple
 * places at once — concurrent calls share the same connection attempt.
 * Rejects if QZ Tray is not installed/running on the machine.
 */
export const connectQz = async (): Promise<void> => {
  if (isQzConnected()) return

  if (connectingPromise) {
    return connectingPromise
  }

  configureSecurity()

  connectingPromise = qz.websocket.connect({ retries: 2, delay: 1 })
  try {
    await connectingPromise
  } finally {
    connectingPromise = null
  }
}

/** Lists printer names known to QZ Tray on this machine. Connects if needed. */
export const listPrinters = async (): Promise<string[]> => {
  await connectQz()
  return qz.printers.find()
}
